﻿/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ShareModal } from "./speedtest/ShareModal";
import { TestProgress } from "./speedtest/TestProgress";
import { PullToRefresh } from "./common/PullToRefresh";
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation";
import { useTabNavigationContext } from "@/context/tabNavigation";
import { useFocus, useReportSelected } from "@/utils/sidebarFocus";
import { DashboardTab } from "./speedtest/DashboardTab";
import { NetworkOverview } from "./speedtest/NetworkOverview";
import { LiveRequestsPanel } from "./requests/LiveRequestsPanel";
import { PageHeader } from "@/components/common/PageHeader";
import { SpeedTestTab } from "./speedtest/SpeedTestTab";
import { TracerouteTab } from "./speedtest/TracerouteTab";
import { ServersTab } from "./servers/ServersTab";
import { SNMPTab } from "./snmp/SNMPTab";
import { DockerTab } from "./docker/DockerTab";
import { HomeTab } from "./homepage/HomeTab";
import { RoutersTab } from "./routers/RoutersTab";
import { showToast } from "@/components/common/Toast";
import { Button } from "@/components/ui/Button";
import { SiDocker } from "react-icons/si";
import {
  PlayIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  SignalIcon,
  ServerIcon,
  ServerStackIcon,
  HomeIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import {
  Server,
  SpeedTestResult,
  TestProgress as TestProgressType,
  TimeRange,
  TestOptions,
  PaginatedResponse,
  Schedule,
} from "@/types/types";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  getServers,
  getHistory,
  getSchedules,
  runSpeedTest,
  getSpeedTestStatus,
  getPublicHistory,
} from "@/api/speedtest";
import { settingsApi } from "@/api/settings";
import { motion, AnimatePresence } from "motion/react";
import { useLanguage } from "@/utils/language";

interface MainProps {
  isPublic?: boolean;
}

export default function Main({ isPublic = false }: MainProps) {
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<TestOptions>({
    enableDownload: true,
    enableUpload: true,
    enableJitter: true,
    multiServer: false,
    useIperf: false,
    useLibrespeed: false,
    serverIds: [],
  });
  const [testType, setTestType] = useState<
    "speedtest" | "iperf" | "librespeed"
  >("speedtest");
  const [selectedServers, setSelectedServers] = useState<Server[]>([]);
  const [progress, setProgress] = useState<TestProgressType | null>(null);
  const [testStatus, setTestStatus] = useState<"idle" | "running" | "complete">(
    "idle"
  );
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    const saved = localStorage.getItem("speedtest-time-range");
    return (saved as TimeRange) || "1w";
  });
  const [isLoading, setIsLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("nyrvex-active-tab");
    if (saved === "packetloss" || saved === "dashboard" || saved === "vpn") {
      localStorage.setItem("nyrvex-active-tab", "home");
      return "home";
    }
    if (saved === "speedtest" || saved === "traceroute") {
      localStorage.setItem("nyrvex-active-tab", "network");
      return "network";
    }
    return saved || "home";
  });
  const [networkTool, setNetworkTool] = useState<"overview" | "speedtest" | "traceroute" | "requests">("overview");

  // Sidebar Network sub-items pick the tool.
  useFocus("network", (tool) => {
    if (tool === "overview" || tool === "speedtest" || tool === "traceroute" || tool === "requests") setNetworkTool(tool);
  });

  useReportSelected("network", activeTab === "network" ? networkTool : null);

  // Tab configuration
  const tabs = [
    {
      id: "home",
      label: t("home"),
      shortLabel: t("home"),
      icon: <HomeIcon className="w-5 h-5" />,
    },
    {
      id: "routers",
      label: t("routers"),
      shortLabel: t("routers"),
      icon: <WifiIcon className="w-5 h-5" />,
    },
    {
      id: "monitor",
      label: t("servers"),
      shortLabel: t("servers"),
      icon: <ServerIcon className="w-5 h-5" />,
    },
    {
      id: "docker",
      label: t("docker"),
      shortLabel: t("docker"),
      icon: <SiDocker className="w-5 h-5" />,
    },
    {
      id: "network",
      label: t("network"),
      shortLabel: t("network"),
      icon: <GlobeAltIcon className="w-5 h-5" />,
    },
{
      id: "switches",
      label: t("switches"),
      shortLabel: t("switches"),
      icon: <ServerStackIcon className="w-5 h-5" />,
    },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    localStorage.setItem("nyrvex-active-tab", tabId);
  };

  // Mobile touch affordances: swipe left/right between tabs, pull-to-refresh.
  const contentRef = useRef<HTMLDivElement>(null);
  useSwipeNavigation(contentRef);
  const refreshAll = useCallback(
    () => queryClient.invalidateQueries(),
    [queryClient]
  );

  // Publish the tab list/state so App's header can render the navbar --
  // Main owns this state, App renders the actual <TabNavigation>.
  const { setNav } = useTabNavigationContext();
  useEffect(() => {
    setNav({ tabs, activeTab, onTabChange: handleTabChange, visible: !isPublic });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isPublic]);

  // Queries
  const { data: speedtestServers = [] } = useQuery({
    queryKey: ["servers", "speedtest"],
    queryFn: () => getServers("speedtest"),
    enabled: !isPublic,
  }) as { data: Server[] };

  const { data: librespeedServers = [], isLoading: isLibrespeedLoading, isError: isLibrespeedError } = useQuery({
    queryKey: ["servers", "librespeed"],
    queryFn: () => getServers("librespeed"),
    enabled: !isPublic,
  }) as { data: Server[]; isLoading: boolean; isError: boolean };

  const allServers = useMemo(
    () => [...speedtestServers, ...librespeedServers],
    [speedtestServers, librespeedServers]
  );

  const servers = useMemo(() => {
    if (testType === "librespeed") return librespeedServers;
    if (testType === "iperf") return []; // or fetch iperf servers if they are separate
    return speedtestServers;
  }, [testType, speedtestServers, librespeedServers]);

  const { data: dashboardSettings } = useQuery({
    queryKey: ["dashboard-settings"],
    queryFn: settingsApi.getDashboardSettings,
    enabled: !isPublic,
    staleTime: 60_000,
  });

  const recentSpeedtestsRowsLimit = isPublic
    ? 20
    : dashboardSettings?.recentSpeedtestsRows ?? 20;

  const { data: historyData } = useInfiniteQuery({
    queryKey: ["history", timeRange, isPublic, recentSpeedtestsRowsLimit],
    queryFn: async ({ pageParam = 1 }) => {
      const historyFn = isPublic ? getPublicHistory : getHistory;
      const response = await historyFn(timeRange, pageParam, recentSpeedtestsRowsLimit);
      return response as PaginatedResponse<SpeedTestResult>;
    },
    getNextPageParam: (
      lastPage: PaginatedResponse<SpeedTestResult> | undefined
    ) => {
      if (!lastPage?.data) return undefined;
      if (lastPage.data.length < lastPage.limit) return undefined;
      return lastPage.page + 1;
    },
    initialPageParam: 1,
    staleTime: 0,
    placeholderData: (previousData) => previousData,
  });

  const history = useMemo(() => {
    if (!historyData?.pages) return [];
    return historyData.pages.flatMap(
      (page) => page?.data ?? []
    ) as SpeedTestResult[];
  }, [historyData]);

  // Query to get all-time history for latest run display and existence check
  const { data: allTimeHistoryData } = useInfiniteQuery({
    queryKey: ["history", "all", isPublic],
    queryFn: async ({ pageParam = 1 }) => {
      const historyFn = isPublic ? getPublicHistory : getHistory;
      const response = await historyFn("all", pageParam, 20); // Get more results for latest run display
      return response as PaginatedResponse<SpeedTestResult>;
    },
    getNextPageParam: () => undefined, // Only fetch first page
    initialPageParam: 1,
    staleTime: 60000, // Cache for 1 minute
  });

  const allTimeHistory = useMemo(() => {
    if (!allTimeHistoryData?.pages) return [];
    return allTimeHistoryData.pages.flatMap(
      (page) => page?.data ?? []
    ) as SpeedTestResult[];
  }, [allTimeHistoryData]);

  const hasAnyTests = useMemo(() => {
    return allTimeHistory.length > 0;
  }, [allTimeHistory]);

  // Use current time range history if available, otherwise fall back to all-time history for latest run
  const latestTest = useMemo(() => {
    return history && history.length > 0
      ? history[0]
      : allTimeHistory.length > 0
      ? allTimeHistory[0]
      : null;
  }, [history, allTimeHistory]);

  const { data: schedules = [] } = useQuery({
    queryKey: ["schedules"],
    queryFn: () => {
      console.log("[Main] Fetching schedules...");
      return getSchedules();
    },
    refetchInterval: 30000, // Refetch every 30 seconds (same as ScheduleManager)
    staleTime: 10000, // Consider data stale after 10 seconds
    enabled: !isPublic,
  }) as { data: Schedule[] };

  // Log when schedules data changes
  useEffect(() => {
    console.log("[Main] Schedules data updated:", schedules);
  }, [schedules]);

  // Mutations
  const speedTestMutation = useMutation({
    mutationFn: runSpeedTest,
    onMutate: () => {
      console.log("Starting speed test with options:", options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["history-chart"] });
      setProgress(null);
      setTestStatus("complete");
    },
    onError: (error: Error) => {
      setError(error.message);
      setTestStatus("idle");
      console.error("Speed test error:", error);
    },
  });

  const handleServerSelect = (server: Server) => {
    setSelectedServers((prev) => {
      const isSelected = prev.some((s) => s.id === server.id);
      if (!options.multiServer) {
        return isSelected ? [] : [server];
      }
      return isSelected
        ? prev.filter((s) => s.id !== server.id)
        : [...prev, server];
    });
  };

  const runTest = async () => {
    if (selectedServers.length === 0) {
      setError("Please select at least one server");
      showToast("Please select a server", "warning");
      return;
    }

    setError(null);
    setTestStatus("running");
    setIsLoading(true);

    // Initialize progress with correct type flags
    if (testType === "iperf") {
      setProgress({
        currentServer: selectedServers[0].name,
        currentTest: "download",
        currentSpeed: 0,
        isComplete: false,
        type: "download",
        speed: 0,
        latency: 0,
        isScheduled: false,
        progress: 0,
        isIperf: true,
        isLibrespeed: false,
      });
    } else if (testType === "librespeed") {
      setProgress({
        currentServer: selectedServers[0].name,
        currentTest: "running",
        currentSpeed: 0,
        isComplete: false,
        type: "running",
        speed: 0,
        latency: 0,
        isScheduled: false,
        progress: 0,
        isIperf: false,
        isLibrespeed: true,
      });
    }

    try {
      // Show toast when test starts
      const testTypeName =
        testType === "iperf"
          ? "iperf3"
          : testType === "librespeed"
          ? "Librespeed"
          : "Speedtest";
      showToast(`${testTypeName} test started`, "success", {
        description: `Testing ${selectedServers[0].host}`,
      });

      await speedTestMutation.mutateAsync({
        ...options,
        useIperf: testType === "iperf",
        useLibrespeed: testType === "librespeed",
        serverIds:
          testType === "speedtest" || testType === "librespeed"
            ? selectedServers.map((s) => s.id)
            : [],
        serverHost: testType === "iperf" ? selectedServers[0].host : undefined,
        serverName: testType === "iperf" ? selectedServers[0].name : undefined,
        isPublicServer: testType === "librespeed" ? (selectedServers[0]?.isPublic ?? false) : false,
      });
    } catch (error) {
      console.error("Error running test:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setError(errorMessage);
      setTestStatus("idle");
      showToast("Failed to start speed test", "error", {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (testStatus === "running") {
      const pollInterval = setInterval(async () => {
        try {
          const update = await getSpeedTestStatus();

          if (update) {
            // Debug log for test updates
            if (
              update.testType === "iperf3" ||
              update.testType === "speedtest"
            ) {
              console.log(`${update.testType} update:`, {
                type: update.type,
                speed: update.speed,
                progress: update.progress,
                isComplete: update.isComplete,
                testType: update.testType,
                serverName: update.serverName,
                timestamp: new Date().toISOString(),
              });
            }

            setProgress((prev) => {
              const baseProgress = {
                currentServer: update.serverName || "",
                currentTest: update.type,
                currentSpeed: update.speed || 0,
                isComplete: update.isComplete,
                type: update.type,
                speed: update.speed || 0,
                latency:
                  typeof update.latency === "string"
                    ? parseFloat(update.latency)
                    : update.latency || 0,
                isScheduled: update.isScheduled,
                progress: update.progress || 0,
                // Preserve isIperf/isLibrespeed state if testType is not provided
                isIperf: update.testType
                  ? update.testType === "iperf3"
                  : prev?.isIperf || false,
                isLibrespeed: update.testType
                  ? update.testType === "librespeed"
                  : prev?.isLibrespeed || false,
              };

              if (!prev) {
                return baseProgress;
              }

              // For iperf3 and speedtest.net, always update to show live progress
              // The backend already throttles updates to once per second

              return {
                ...prev,
                ...baseProgress,
              };
            });

            if (update.isComplete && update.type !== "download") {
              setTestStatus("complete");
              setProgress(null);
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["history"] }),
                queryClient.invalidateQueries({ queryKey: ["history-chart"] }),
                queryClient.invalidateQueries({ queryKey: ["schedules"] }),
              ]);
              // Show success toast with test results
              if (update.download && update.upload) {
                showToast("Speed test completed", "success", {
                  description: `Download: ${update.download.toFixed(
                    2
                  )} Mbps, Upload: ${update.upload.toFixed(2)} Mbps`,
                });
              }
            }
          }
        } catch (error) {
          console.error("Test status check error:", error);
        }
      }, 2000); // Poll every 2 seconds to match both speedtest.net and iperf3

      return () => clearInterval(pollInterval);
    }
  }, [testStatus, queryClient, testType]);

  return (
    <div className="min-h-screen">
      <main className="w-full max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 pb-[calc(6.25rem+var(--ios-bottom-safe))] sm:pb-8 pt-[calc(4.25rem+env(safe-area-inset-top))] sm:pt-3 md:pt-3">
        {/* Test Progress - Always rendered with fixed height to prevent layout shift */}
        <div className="hidden sm:flex justify-center relative z-10 sm:h-0 sm:overflow-visible">
          <TestProgress progress={progress} />
        </div>

        {/* Header - Now just an empty spacer */}
        <div className="mb-2 sm:mb-0" />

        {/* Share Modal */}
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
        />

        {/* Error Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              key="error-message"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="relative bg-red-50 dark:bg-red-950/20 border border-red-400/60 dark:border-red-800/60 border-l-2 border-l-red-500 mb-4 overflow-hidden"
            >
              <div className="relative flex items-start justify-between p-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg
                      className="w-5 h-5 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-red-800 dark:text-red-300 mb-1">
                      Error
                    </h3>
                    <div className="text-sm text-red-700/90 dark:text-red-300/90 break-words whitespace-pre-wrap">
                      {(() => {
                        try {
                          // Try to extract and format JSON from error messages
                          const jsonMatch = error.match(/(\{[\s\S]*\})/);
                          if (jsonMatch) {
                            const jsonStr = jsonMatch[1];
                            const parsed = JSON.parse(jsonStr);
                            const formatted = JSON.stringify(parsed, null, 2);
                            return error.replace(jsonMatch[1], formatted);
                          }
                          return error;
                        } catch {
                          return error;
                        }
                      })()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="flex-shrink-0 ml-4 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200"
                  aria-label="Dismiss error"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tab Content */}
        <div ref={contentRef}>
        <PullToRefresh onRefresh={refreshAll}>
        <AnimatePresence mode="wait">
          {!isPublic && activeTab === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <HomeTab />
            </motion.div>
          )}

          {(activeTab === "dashboard" || isPublic) && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <DashboardTab
                latestTest={latestTest}
                tests={history}
                recentSpeedtestsRows={recentSpeedtestsRowsLimit}
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                isPublic={isPublic}
                hasAnyTests={hasAnyTests}
                onShareClick={() => setShareModalOpen(true)}
                onNavigateToSpeedTest={() => {
                  setNetworkTool("speedtest");
                  handleTabChange("network");
                }}
                onNavigateToVnstat={(agentId?: number) => {
                  handleTabChange("monitor");
                  if (agentId) {
                    sessionStorage.setItem(
                      "nyrvex-preselect-agent",
                      agentId.toString()
                    );
                  }
                }}
              />
            </motion.div>
            )}

          {!isPublic && activeTab === "network" && (
            <motion.div
              key="network"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <div className="space-y-5">
                <PageHeader
                  title={t("network")}
                  subtitle={t("networkSubtitle")}
                  actions={
                            <div className="grid grid-cols-4 gap-2 rounded-lg border border-gray-200 bg-gray-100/80 p-1 dark:border-gray-800 dark:bg-gray-900/70 sm:w-auto">
                              <Button
                                variant={networkTool === "overview" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setNetworkTool("overview")}
                                className="gap-1.5"
                              >
                                <Squares2X2Icon className="h-4 w-4" />
                                Overview
                              </Button>
                              <Button
                                variant={networkTool === "speedtest" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setNetworkTool("speedtest")}
                                className="gap-1.5"
                              >
                                <PlayIcon className="h-4 w-4" />
                                {t("speedTest")}
                              </Button>
                              <Button
                                variant={networkTool === "traceroute" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setNetworkTool("traceroute")}
                                className="gap-1.5"
                              >
                                <GlobeAltIcon className="h-4 w-4" />
                                {t("traceroute")}
                              </Button>
<Button
                                variant={networkTool === "requests" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setNetworkTool("requests")}
                                className="gap-1.5"
                              >
                                <SignalIcon className="h-4 w-4" />
                                Requests
                              </Button>
                            </div>
                  }
                />
                <div className="sm:hidden">
                          <div className="grid grid-cols-4 gap-2 rounded-lg border border-gray-200 bg-gray-100/80 p-1 dark:border-gray-800 dark:bg-gray-900/70 sm:w-auto">
                            <Button
                              variant={networkTool === "overview" ? "default" : "ghost"}
                              size="sm"
                              onClick={() => setNetworkTool("overview")}
                              className="gap-1.5"
                            >
                              <Squares2X2Icon className="h-4 w-4" />
                              Overview
                            </Button>
                            <Button
                              variant={networkTool === "speedtest" ? "default" : "ghost"}
                              size="sm"
                              onClick={() => setNetworkTool("speedtest")}
                              className="gap-1.5"
                            >
                              <PlayIcon className="h-4 w-4" />
                              {t("speedTest")}
                            </Button>
                            <Button
                              variant={networkTool === "traceroute" ? "default" : "ghost"}
                              size="sm"
                              onClick={() => setNetworkTool("traceroute")}
                              className="gap-1.5"
                            >
                              <GlobeAltIcon className="h-4 w-4" />
                              {t("traceroute")}
                            </Button>
<Button
                                variant={networkTool === "requests" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setNetworkTool("requests")}
                                className="gap-1.5"
                              >
                                <SignalIcon className="h-4 w-4" />
                                Requests
                              </Button>
                          </div>
                </div>

                {networkTool === "overview" ? (
                  <NetworkOverview onOpen={setNetworkTool} />
                ) : networkTool === "speedtest" ? (
                  <SpeedTestTab
                    servers={servers}
                    selectedServers={selectedServers}
                    onServerSelect={handleServerSelect}
                    options={options}
                    onOptionsChange={setOptions}
                    testType={testType}
                    onTestTypeChange={setTestType}
                    isLoading={isLoading}
                    onRunTest={runTest}
                    progress={progress}
                    allServers={allServers}
                    isServersLoading={testType === "librespeed" ? isLibrespeedLoading : false}
                    isServersError={testType === "librespeed" ? isLibrespeedError : false}
                  />
                ) : networkTool === "requests" ? (
                  <LiveRequestsPanel />
                ) : (
                  <TracerouteTab />
                )}
              </div>
            </motion.div>
          )}

          {!isPublic && activeTab === "monitor" && (
            <motion.div
              key="monitor"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <ServersTab />
            </motion.div>
          )}

          {!isPublic && activeTab === "switches" && (
            <motion.div
              key="switches"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <SNMPTab />
            </motion.div>
          )}

          {!isPublic && activeTab === "docker" && (
            <motion.div
              key="docker"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <DockerTab />
            </motion.div>
          )}

{!isPublic && activeTab === "routers" && (
            <motion.div
              key="routers"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <RoutersTab />
            </motion.div>
          )}

        </AnimatePresence>
        </PullToRefresh>
        </div>
      </main>
    </div>
  );
}

